import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getUrl } from 'aws-amplify/storage';

interface FoodItem {
  name: string;
  calories: number;
}

interface MealData {
  mealTimestamp: string;
  totalCalories: number;
  confidenceLevel: string;
  foodItems: FoodItem[];
  imageKey: string;
  timestamp: number;
  date: string;
  dishName?: string;
  suggestedCalories?: number;
}

const MealAnalysis: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mealData, setMealData] = useState<MealData | null>(location.state?.mealData || null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(!location.state?.mealData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If meal data wasn't passed in location state, redirect to history
    if (!mealData) {
      navigate('/history');
    }
  }, [mealData, navigate]);

  useEffect(() => {
    // Fetch image from S3 if we have meal data with an image key
    if (mealData?.imageKey) {
      const fetchImage = async () => {
        try {
          const { url } = await getUrl({
            key: mealData.imageKey,
            options: {
              contentType: 'image/jpeg',
              accessLevel: 'private',
              expiresIn: 3600 // 1 hour expiration
            }
          });
          setImageUrl(url.toString());
        } catch (error) {
          console.error('Error fetching image:', error);
          // Don't set error state here, as the meal data is still useful without the image
        }
      };

      fetchImage();
    }
  }, [mealData]);

  // Helper to get CSS class for confidence level
  const getConfidenceClass = (level: string): string => {
    switch (level.toLowerCase()) {
      case 'high': return 'confidence-high';
      case 'medium': return 'confidence-medium';
      case 'low': return 'confidence-low';
      default: return 'confidence-low';
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error || !mealData) {
    return (
      <div className="card">
        <h2 className="card-title">Error</h2>
        <div className="error-message">
          {error || 'No meal data available'}
        </div>
        <button className="btn" onClick={() => navigate('/history')}>
          Go to Meal History
        </button>
      </div>
    );
  }

  // Format date and time
  const mealDate = new Date(mealData.timestamp * 1000);
  const formattedDate = mealDate.toLocaleDateString();
  const formattedTime = mealDate.toLocaleTimeString();

  return (
    <div>
      <div className="card">
        <h2 className="card-title">{mealData.dishName || 'Meal Analysis'}</h2>
        <p>
          <strong>Date:</strong> {formattedDate} at {formattedTime}
        </p>
        
        <div className="meal-summary">
          <h3>Total Calories: {mealData.totalCalories}</h3>
          <div className="confidence-level">
            <span>Confidence: </span>
            <span className={`confidence-badge ${getConfidenceClass(mealData.confidenceLevel)}`}>
              {mealData.confidenceLevel}
            </span>
          </div>
        </div>
      </div>
      
      {imageUrl && (
        <div className="card">
          <h3 className="card-title">Food Image</h3>
          <img 
            src={imageUrl} 
            alt="Food" 
            style={{ width: '100%', borderRadius: '8px' }}
          />
        </div>
      )}

      <div className="card">
        <h3 className="card-title">Food Items Detected</h3>
        {mealData.foodItems.length === 0 ? (
          <p>No food items were detected.</p>
        ) : (
          <div className="food-items-list">
            {mealData.foodItems.map((item, index) => (
              <div key={index} className="food-item">
                <span className="food-name">{item.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="action-buttons" style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between' }}>
        <button 
          className="btn btn-secondary" 
          onClick={() => navigate('/history')}
        >
          Back to History
        </button>
        <div>
          <button 
            className="btn" 
            onClick={() => navigate(`/edit-meal`, { state: { mealData } })}
          >
            Edit Analysis
          </button>
        </div>
      </div>
    </div>
  );
};

export default MealAnalysis;
